import { useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { LuBell } from "react-icons/lu"
import { Box, HStack, IconButton, VStack } from "@chakra-ui/react"
import { Text } from "@/components/ui"
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from "@/hooks/notifications/useNotifications"
import type { Notification } from "@/api/notifications"

export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { data: notifications = [] } = useNotifications()
  const { data: unreadCount = 0 } = useUnreadNotificationCount()
  const markRead = useMarkNotificationAsRead()
  const markAllRead = useMarkAllNotificationsAsRead()

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markRead.mutate(notification.id)
    }
    if (notification.dealId) {
      navigate(`/core/deal/${notification.dealId}`)
    }
    setIsOpen(false)
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return "just now"
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDays = Math.floor(diffHr / 24)
    return `${diffDays}d ago`
  }

  const typeIcon = (type: string) => {
    switch (type) {
      case "nda_uploaded": return "\u{1F4DD}"
      case "bcpf_uploaded": return "\u{1F50D}"
      case "grant_extraction_ready": return "\u{1F4CA}"
      case "stage_changed": return "\u{1F4CD}"
      case "portal_doc_uploaded": return "\u{1F4C4}"
      default: return "\u{1F514}"
    }
  }

  return (
    <Box position="relative" ref={ref}>
      <IconButton
        aria-label="Notifications"
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <LuBell />
      </IconButton>

      {unreadCount > 0 && (
        <Box
          position="absolute"
          top="0"
          right="0"
          bg="red.500"
          color="white"
          fontSize="10px"
          fontWeight="bold"
          borderRadius="full"
          w="18px"
          h="18px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
          lineHeight="1"
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </Box>
      )}

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 6px)"
          right={0}
          w="380px"
          maxH="480px"
          overflowY="auto"
          bg="bg.panel"
          border="1px solid"
          borderColor="border.default"
          borderRadius="md"
          shadow="lg"
          zIndex={1000}
        >
          <HStack p={3} borderBottom="1px solid" borderColor="border.default" justifyContent="space-between">
            <Text fontWeight="semibold" fontSize="sm">
              Notifications
            </Text>
            {unreadCount > 0 && (
              <Text
                fontSize="xs"
                color="blue.500"
                cursor="pointer"
                onClick={() => markAllRead.mutate()}
                _hover={{ textDecoration: "underline" }}
              >
                Mark all as read
              </Text>
            )}
          </HStack>

          {notifications.length === 0 ? (
            <Box p={6} textAlign="center">
              <Text fontSize="sm" color="text.muted">
                No notifications yet
              </Text>
            </Box>
          ) : (
            <VStack align="stretch" gap={0}>
              {notifications.map((n) => (
                <HStack
                  key={n.id}
                  p={3}
                  gap={3}
                  cursor="pointer"
                  onClick={() => handleNotificationClick(n)}
                  _hover={{ bg: "hover.bg" }}
                  bg={n.isRead ? "transparent" : "blue.50/5"}
                  borderBottom="1px solid"
                  borderColor="border.default"
                >
                  <Text fontSize="lg" flexShrink={0}>
                    {typeIcon(n.type)}
                  </Text>
                  <VStack align="start" gap={0.5} flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight={n.isRead ? "normal" : "semibold"} lineClamp={1}>
                      {n.title}
                    </Text>
                    <Text fontSize="xs" color="text.muted" lineClamp={1}>
                      {n.message}
                    </Text>
                  </VStack>
                  <VStack align="end" gap={1} flexShrink={0}>
                    <Text fontSize="xs" color="text.muted" whiteSpace="nowrap">
                      {formatTime(n.createdAt)}
                    </Text>
                    {!n.isRead && (
                      <Box w="8px" h="8px" borderRadius="full" bg="blue.500" />
                    )}
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  )
}
